'use client'
import Cookies, { CookieChangeListener } from 'universal-cookie'
import setUtmCookies from '@/middleware/utmParamsMiddleware/setUtmCookies'
import { TRACKING_COOKIE_NAME, TrackingCookieType } from './types'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'

const cookies = new Cookies()

function useTrackingCookie(serverSideCookieValue: TrackingCookieType, domain: string) {
  const [trackingCookieValue, setTrackingCookieValue] = useState<TrackingCookieType>(serverSideCookieValue)
  const searchParams = useSearchParams()

  const handleCookieChange: CookieChangeListener = useCallback((cookie) => {
    if (cookie.name !== TRACKING_COOKIE_NAME) {
      return
    }
    setTrackingCookieValue(cookie.value as TrackingCookieType)
  }, [])

  const setCookie = useCallback(
    (value: TrackingCookieType) => {
      cookies.set(TRACKING_COOKIE_NAME, value, {
        path: '/',
        expires: new Date(2099, 1, 1),
        domain: `.${domain}`,
      })
      setTrackingCookieValue(value)
      if (value !== 'accepted' || !searchParams) return
      setUtmCookies(cookies, domain, searchParams)
    },
    [searchParams, domain]
  )

  useEffect(() => {
    const value = cookies.get<TrackingCookieType | 'yes' | 'no'>(TRACKING_COOKIE_NAME)
    if (value && ['yes', 'no', 'accepted'].includes(value)) {
      cookies.remove(TRACKING_COOKIE_NAME, { path: '/' })
      if (value === 'no') return
      setCookie('accepted')
    }
  }, [setCookie])

  useEffect(() => {
    const value = cookies.get<TrackingCookieType>(TRACKING_COOKIE_NAME)
    setTrackingCookieValue(value)
    cookies.addChangeListener(handleCookieChange)
    return () => {
      cookies.removeChangeListener(handleCookieChange)
    }
  }, [handleCookieChange])

  return [trackingCookieValue, setCookie] as const
}

export default useTrackingCookie
